package com.bikompanion.ui.composables

import androidx.compose.runtime.Composable
import com.bikompanion.domain.BicingNetworkId

/** The operator's own app for a bike network, where we know of one. */
internal data class OperatorApp(
    val name: String,
    val androidPackage: String,
)

/**
 * Operator apps by CityBikes network id.
 *
 * Nothing derives these: the API describes networks and stations, never app stores, so every
 * entry is added by hand for a network worth the maintenance. Networks missing from here simply
 * don't offer the button.
 */
internal val OperatorApps = mapOf(
    // Smou runs Bicing and AMBici alike, so both routes end at the same app.
    BicingNetworkId to OperatorApp(name = "Bicing", androidPackage = "cat.bcn.smoubcn"),
)

/**
 * The app to offer for a network, or `null` when we know of none.
 *
 * Matched by id first. Ambici is matched on the id *or* the name containing "ambici", because
 * CityBikes splits it into a network per municipality — `ambici-cornella-de-llobregat`,
 * `ambici-gava`, and a dozen more — rather than one id worth listing above. Matching the id
 * matters as much as the name: the name only arrives once the networks list has loaded, while
 * a station always knows the id it came from.
 */
internal fun operatorAppFor(
    networkId: String,
    networkName: String?,
): OperatorApp? =
    OperatorApps[networkId]
        ?: OperatorApps.getValue(BicingNetworkId).takeIf { networkId.isAmbici() || networkName?.isAmbici() == true }

/**
 * Whether a network is one of AMBici's per-municipality splits, which offer two routes to a
 * station — AMBici's own site, linked from the API, and the Bicing app that also serves them.
 */
internal fun isAmbici(
    networkId: String,
    networkName: String?,
): Boolean = networkId.isAmbici() || networkName?.isAmbici() == true

private fun String.isAmbici() = contains("ambici", ignoreCase = true)

/**
 * An action that opens [operatorApp], or `null` when there is nothing to open — an unknown
 * network, or a platform with no notion of launching another app.
 */
@Composable
internal expect fun rememberOperatorAppLauncher(operatorApp: OperatorApp?): (() -> Unit)?
